import React, { useState, useEffect } from "react";
import { useNavigate, useParams, useLocation, Link } from "react-router-dom";
import Swal from "sweetalert2";
import { useDispatch } from "react-redux";

import MainLayout from "../../../../components/layout/MainLayout";
import Breadcrumbs from "../../../../components/common/Breadcrumbs";
import ReferralForm from "./ReferralForm";

import { fetchReferralForm, updateReferralForm } from "../../../../store/slices/referralFormsSlice";

// Convert snake_case to camelCase
const toCamelCase = (obj) => {
    const newObj = {};
    Object.keys(obj).forEach((key) => {
        const camelKey = key.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
        newObj[camelKey] = obj[key];
    });
    return newObj;
};

// Convert camelCase to snake_case
const toSnakeCase = (obj) => {
    const newObj = {};
    Object.keys(obj).forEach((key) => {
        const snakeKey = key.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
        newObj[snakeKey] = obj[key];
    });
    return newObj;
};

const EditReferral = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { id } = useParams();
    const { state } = useLocation();
    const { caseId, patientId } = state || {};

    const [formData, setFormData] = useState({
        date: "",
        referringFacility: "",
        patientName: "",
        dob: "",
        gender: "",
        contact: "",
        primaryComplaintDiagnosis: "",
        currentMedication: "",
        historyPreviousTreatment: "",
        furtherManagement: "",
        otherManagement: "",
        referredTo: "",
        departmentConsultant: "",
        acknowledgmentAccepted: false,
        acknowledgmentName: "",
        acknowledgmentRelation: "",
        acknowledgmentDateTime: ""
    });

    // Fetch referral data on mount
    useEffect(() => {
        const loadReferral = async () => {
            try {
                const response = await dispatch(fetchReferralForm(id)).unwrap();
                const camelData = toCamelCase(response);
                setFormData(prev => ({ ...prev, ...camelData }));
            } catch (err) {
                Swal.fire("Error", err?.message || "Failed to fetch referral data", "error");
            }
        };
        loadReferral();
    }, [id, dispatch]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === "checkbox" ? checked : value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        try {
            // Destructure the fields we need to check
            const { furtherManagement, otherManagement, ...restData } = formData;

            // Prepare data to send
            let dataToSend = { ...restData, furtherManagement };

            // Only include otherManagement if furtherManagement is "Other"
            if (furtherManagement === "Other") {
                dataToSend.otherManagement = otherManagement;
            }

            // Convert to snake_case for backend
            const payload = toSnakeCase(dataToSend);

            await dispatch(updateReferralForm({ case_id: caseId, referralData: payload })).unwrap();

            Swal.fire({
                title: "Updated!",
                text: "Referral form updated successfully.",
                icon: "success",
                timer: 2000,
                showConfirmButton: false
            });

            navigate(`/cases/${caseId}/profile`, { state: { patientId, caseId } });
        } catch (err) {
            Swal.fire("Error", err?.message || "Failed to update referral form", "error");
        }
    };

    return (
        <MainLayout>
            <div className="container-xxl flex-grow-1 container-p-y">
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <Breadcrumbs />
                    <Link
                        to={`/optional/referral-form/print/${id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-secondary"
                    >
                        <i className="ri-printer-line me-1"></i> Print
                    </Link>
                </div>

                <div className="card">
                    <div className="card-body">
                        <form onSubmit={handleSubmit}>
                            <ReferralForm formData={formData} handleChange={handleChange} />

                            <div className="col-12 d-flex justify-content-end mt-3">
                                <div className="col-md-2 d-flex justify-content-end gap-2">
                                    <button type="submit" className="btn btn-primary px-4">
                                        <i className="ri-edit-2-line me-1"></i>
                                        Update
                                    </button>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </MainLayout>
    );
};

export default EditReferral;
